/**
 * DPL Jade Symbol Library
 *
 * Each symbol has:
 *   id         — unique key
 *   label      — display name
 *   layer      — which layer it belongs to
 *   category   — grouping for picker UI
 *   size       — bounding box in mm (width × height at 1:1)
 *   snapToWall — if true, symbol auto-orients perpendicular to nearest wall
 *   svgPath    — inline SVG path/group to draw the symbol at (0,0), size×size box
 *                all paths assume a 100×100 viewBox, scaled to `size` on placement
 */

export const SYMBOL_LIBRARY = {

  // ── ELECTRICAL ─────────────────────────────────────────────────────────────
  POWER_13A: {
    id: "POWER_13A", label: "13A Power Point", layer: "electrical", category: "Electrical",
    size: 200, snapToWall: true,
    svgPath: `<circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" stroke-width="5"/>
              <line x1="35" y1="35" x2="35" y2="65" stroke="currentColor" stroke-width="5"/>
              <line x1="65" y1="35" x2="65" y2="65" stroke="currentColor" stroke-width="5"/>
              <line x1="50" y1="68" x2="50" y2="78" stroke="currentColor" stroke-width="5"/>`,
  },
  POWER_5A: {
    id: "POWER_5A", label: "5A Light Point", layer: "electrical", category: "Electrical",
    size: 200, snapToWall: true,
    svgPath: `<circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" stroke-width="5"/>
              <line x1="35" y1="40" x2="35" y2="60" stroke="currentColor" stroke-width="4"/>
              <line x1="65" y1="40" x2="65" y2="60" stroke="currentColor" stroke-width="4"/>`,
  },
  SWITCH_1: {
    id: "SWITCH_1", label: "1-Way Switch", layer: "electrical", category: "Electrical",
    size: 150, snapToWall: true,
    svgPath: `<rect x="20" y="30" width="60" height="40" rx="4" fill="none" stroke="currentColor" stroke-width="5"/>
              <circle cx="50" cy="50" r="6" fill="currentColor"/>
              <line x1="80" y1="50" x2="100" y2="50" stroke="currentColor" stroke-width="5"/>`,
  },
  SWITCH_2: {
    id: "SWITCH_2", label: "2-Way Switch", layer: "electrical", category: "Electrical",
    size: 150, snapToWall: true,
    svgPath: `<rect x="10" y="30" width="80" height="40" rx="4" fill="none" stroke="currentColor" stroke-width="5"/>
              <circle cx="38" cy="50" r="5" fill="currentColor"/>
              <circle cx="62" cy="50" r="5" fill="currentColor"/>
              <line x1="90" y1="50" x2="100" y2="50" stroke="currentColor" stroke-width="5"/>`,
  },
  LIGHT_CEILING: {
    id: "LIGHT_CEILING", label: "Ceiling Light", layer: "electrical", category: "Electrical",
    size: 400, snapToWall: false,
    svgPath: `<circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" stroke-width="5"/>
              <circle cx="50" cy="50" r="8"  fill="currentColor"/>
              <line x1="50" y1="5"  x2="50" y2="20" stroke="currentColor" stroke-width="4"/>
              <line x1="50" y1="80" x2="50" y2="95" stroke="currentColor" stroke-width="4"/>
              <line x1="5"  y1="50" x2="20" y2="50" stroke="currentColor" stroke-width="4"/>
              <line x1="80" y1="50" x2="95" y2="50" stroke="currentColor" stroke-width="4"/>`,
  },
  LIGHT_EXHAUST: {
    id: "LIGHT_EXHAUST", label: "Exhaust Fan", layer: "electrical", category: "Electrical",
    size: 300, snapToWall: true,
    svgPath: `<circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" stroke-width="5"/>
              <path d="M50,12 A38,38 0 0,1 88,50 L50,50 Z" fill="currentColor" opacity="0.4"/>
              <path d="M88,50 A38,38 0 0,1 50,88 L50,50 Z" fill="currentColor" opacity="0.4"/>
              <path d="M50,88 A38,38 0 0,1 12,50 L50,50 Z" fill="currentColor" opacity="0.4"/>
              <path d="M12,50 A38,38 0 0,1 50,12 L50,50 Z" fill="currentColor" opacity="0.4"/>
              <circle cx="50" cy="50" r="8" fill="currentColor"/>`,
  },
  DB_BOARD: {
    id: "DB_BOARD", label: "Distribution Board", layer: "electrical", category: "Electrical",
    size: 300, snapToWall: true,
    svgPath: `<rect x="15" y="20" width="70" height="60" rx="3" fill="none" stroke="currentColor" stroke-width="6"/>
              <line x1="15" y1="38" x2="85" y2="38" stroke="currentColor" stroke-width="3"/>
              <line x1="15" y1="53" x2="85" y2="53" stroke="currentColor" stroke-width="3"/>
              <line x1="15" y1="68" x2="85" y2="68" stroke="currentColor" stroke-width="3"/>
              <text x="50" y="16" text-anchor="middle" font-size="14" font-family="monospace" fill="currentColor">DB</text>`,
  },
  AC_UNIT: {
    id: "AC_UNIT", label: "AC Unit", layer: "electrical", category: "Electrical",
    size: 900, snapToWall: true,
    svgPath: `<rect x="5" y="25" width="90" height="50" rx="5" fill="none" stroke="currentColor" stroke-width="5"/>
              <line x1="5" y1="42" x2="95" y2="42" stroke="currentColor" stroke-width="3"/>
              <line x1="5" y1="55" x2="95" y2="55" stroke="currentColor" stroke-width="3"/>
              <circle cx="82" cy="35" r="5" fill="currentColor"/>`,
  },

  // ── PLUMBING ───────────────────────────────────────────────────────────────
  WC: {
    id: "WC", label: "WC / Toilet", layer: "plumbing", category: "Plumbing",
    size: 750, snapToWall: true,
    svgPath: `<ellipse cx="50" cy="62" rx="35" ry="28" fill="none" stroke="currentColor" stroke-width="5"/>
              <ellipse cx="50" cy="62" rx="22" ry="17" fill="none" stroke="currentColor" stroke-width="3"/>
              <rect x="22" y="25" width="56" height="22" rx="4" fill="none" stroke="currentColor" stroke-width="5"/>`,
  },
  BASIN: {
    id: "BASIN", label: "Wash Basin", layer: "plumbing", category: "Plumbing",
    size: 600, snapToWall: true,
    svgPath: `<path d="M15,20 L85,20 Q92,20 92,40 L92,75 Q92,88 50,88 Q8,88 8,75 L8,40 Q8,20 15,20 Z"
                fill="none" stroke="currentColor" stroke-width="5"/>
              <circle cx="50" cy="58" r="10" fill="none" stroke="currentColor" stroke-width="4"/>
              <line x1="42" y1="20" x2="40" y2="10" stroke="currentColor" stroke-width="4"/>
              <line x1="58" y1="20" x2="60" y2="10" stroke="currentColor" stroke-width="4"/>`,
  },
  SHOWER: {
    id: "SHOWER", label: "Shower", layer: "plumbing", category: "Plumbing",
    size: 900, snapToWall: false,
    svgPath: `<rect x="10" y="10" width="80" height="80" rx="5" fill="none" stroke="currentColor" stroke-width="5"/>
              <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="4" stroke-dasharray="6 4"/>
              <circle cx="50" cy="50" r="5"  fill="currentColor"/>
              <line x1="50" y1="10" x2="50" y2="30" stroke="currentColor" stroke-width="4"/>`,
  },
  BATHTUB: {
    id: "BATHTUB", label: "Bathtub", layer: "plumbing", category: "Plumbing",
    size: 1700, snapToWall: true,
    svgPath: `<rect x="8" y="15" width="84" height="70" rx="20" fill="none" stroke="currentColor" stroke-width="5"/>
              <ellipse cx="50" cy="50" rx="30" ry="22" fill="none" stroke="currentColor" stroke-width="3"/>
              <circle cx="50" cy="28" r="5" fill="none" stroke="currentColor" stroke-width="3"/>`,
  },
  SINK_KITCHEN: {
    id: "SINK_KITCHEN", label: "Kitchen Sink (Double)", layer: "plumbing", category: "Plumbing",
    size: 900, snapToWall: true,
    svgPath: `<rect x="5" y="20" width="90" height="60" rx="3" fill="none" stroke="currentColor" stroke-width="5"/>
              <rect x="8"  y="23" width="40" height="54" rx="3" fill="none" stroke="currentColor" stroke-width="3"/>
              <rect x="52" y="23" width="40" height="54" rx="3" fill="none" stroke="currentColor" stroke-width="3"/>
              <circle cx="28" cy="50" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
              <circle cx="72" cy="50" r="5" fill="none" stroke="currentColor" stroke-width="3"/>`,
  },
  FLOOR_DRAIN: {
    id: "FLOOR_DRAIN", label: "Floor Drain", layer: "plumbing", category: "Plumbing",
    size: 150, snapToWall: false,
    svgPath: `<circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" stroke-width="5"/>
              <circle cx="50" cy="50" r="15" fill="none" stroke="currentColor" stroke-width="3"/>
              <line x1="10" y1="50" x2="90" y2="50" stroke="currentColor" stroke-width="3"/>
              <line x1="50" y1="10" x2="50" y2="90" stroke="currentColor" stroke-width="3"/>`,
  },

  // ── FURNITURE ──────────────────────────────────────────────────────────────
  BED_SINGLE: {
    id: "BED_SINGLE", label: "Single Bed (900×2000)", layer: "furniture", category: "Furniture",
    size: 900, snapToWall: false,
    svgPath: `<rect x="5"  y="5"  width="90" height="90" rx="4" fill="none" stroke="currentColor" stroke-width="5"/>
              <rect x="5"  y="5"  width="90" height="22" rx="4" fill="currentColor" opacity="0.3"/>
              <ellipse cx="50" cy="58" rx="25" ry="20" fill="currentColor" opacity="0.15"/>`,
  },
  BED_DOUBLE: {
    id: "BED_DOUBLE", label: "Double Bed (1600×2000)", layer: "furniture", category: "Furniture",
    size: 1600, snapToWall: false,
    svgPath: `<rect x="3"  y="3"  width="94" height="94" rx="3" fill="none" stroke="currentColor" stroke-width="4"/>
              <rect x="3"  y="3"  width="94" height="20" rx="3" fill="currentColor" opacity="0.3"/>
              <ellipse cx="28" cy="62" rx="20" ry="26" fill="currentColor" opacity="0.15"/>
              <ellipse cx="72" cy="62" rx="20" ry="26" fill="currentColor" opacity="0.15"/>
              <line x1="50" y1="23" x2="50" y2="97" stroke="currentColor" stroke-width="2" stroke-dasharray="4 3"/>`,
  },
  SOFA_3: {
    id: "SOFA_3", label: "3-Seater Sofa (2100×850)", layer: "furniture", category: "Furniture",
    size: 2100, snapToWall: false,
    svgPath: `<rect x="3" y="30" width="94" height="60" rx="6" fill="none" stroke="currentColor" stroke-width="4"/>
              <rect x="3" y="30" width="94" height="18" rx="6" fill="currentColor" opacity="0.3"/>
              <line x1="36" y1="48" x2="36" y2="90" stroke="currentColor" stroke-width="3"/>
              <line x1="64" y1="48" x2="64" y2="90" stroke="currentColor" stroke-width="3"/>
              <rect x="3" y="48" width="16" height="42" rx="4" fill="currentColor" opacity="0.2"/>
              <rect x="81" y="48" width="16" height="42" rx="4" fill="currentColor" opacity="0.2"/>`,
  },
  DINING_4: {
    id: "DINING_4", label: "Dining Table 4-Seat (1400×800)", layer: "furniture", category: "Furniture",
    size: 1400, snapToWall: false,
    svgPath: `<rect x="18" y="18" width="64" height="64" rx="3" fill="none" stroke="currentColor" stroke-width="4"/>
              <rect x="28" y="5"  width="16" height="13" rx="3" fill="none" stroke="currentColor" stroke-width="3"/>
              <rect x="56" y="5"  width="16" height="13" rx="3" fill="none" stroke="currentColor" stroke-width="3"/>
              <rect x="28" y="82" width="16" height="13" rx="3" fill="none" stroke="currentColor" stroke-width="3"/>
              <rect x="56" y="82" width="16" height="13" rx="3" fill="none" stroke="currentColor" stroke-width="3"/>`,
  },
  CHAIR: {
    id: "CHAIR", label: "Chair (500×500)", layer: "furniture", category: "Furniture",
    size: 500, snapToWall: false,
    svgPath: `<rect x="15" y="35" width="70" height="55" rx="4" fill="none" stroke="currentColor" stroke-width="5"/>
              <rect x="15" y="10" width="70" height="25" rx="4" fill="none" stroke="currentColor" stroke-width="4"/>`,
  },
  KITCHEN_COUNTER: {
    id: "KITCHEN_COUNTER", label: "Kitchen Counter (600 deep)", layer: "furniture", category: "Furniture",
    size: 600, snapToWall: true,
    svgPath: `<rect x="5" y="20" width="90" height="60" rx="3" fill="none" stroke="currentColor" stroke-width="5"/>
              <rect x="5" y="20" width="90" height="10" fill="currentColor" opacity="0.2"/>
              <line x1="5" y1="30" x2="95" y2="30" stroke="currentColor" stroke-width="2"/>`,
  },
  WARDROBE: {
    id: "WARDROBE", label: "Wardrobe (1800×600)", layer: "furniture", category: "Furniture",
    size: 600, snapToWall: true,
    svgPath: `<rect x="5" y="5" width="90" height="90" rx="2" fill="none" stroke="currentColor" stroke-width="5"/>
              <line x1="50" y1="5" x2="50" y2="95" stroke="currentColor" stroke-width="3"/>
              <circle cx="44" cy="50" r="4" fill="currentColor"/>
              <circle cx="56" cy="50" r="4" fill="currentColor"/>`,
  },
  DESK: {
    id: "DESK", label: "Study Desk (1200×600)", layer: "furniture", category: "Furniture",
    size: 600, snapToWall: true,
    svgPath: `<rect x="5" y="15" width="90" height="70" rx="3" fill="none" stroke="currentColor" stroke-width="5"/>
              <rect x="5" y="15" width="35" height="70" rx="2" fill="none" stroke="currentColor" stroke-width="3"/>
              <line x1="40" y1="30" x2="40" y2="85" stroke="currentColor" stroke-width="2"/>`,
  },

  // ── STRUCTURAL ─────────────────────────────────────────────────────────────
  COLUMN_SQUARE: {
    id: "COLUMN_SQUARE", label: "Column (300×300)", layer: "structural", category: "Structural",
    size: 300, snapToWall: false,
    svgPath: `<rect x="10" y="10" width="80" height="80" fill="currentColor" opacity="0.5" stroke="currentColor" stroke-width="5"/>
              <line x1="10" y1="10" x2="90" y2="90" stroke="currentColor" stroke-width="3"/>
              <line x1="90" y1="10" x2="10" y2="90" stroke="currentColor" stroke-width="3"/>`,
  },
  COLUMN_ROUND: {
    id: "COLUMN_ROUND", label: "Column Circular (300 dia)", layer: "structural", category: "Structural",
    size: 300, snapToWall: false,
    svgPath: `<circle cx="50" cy="50" r="40" fill="currentColor" opacity="0.5" stroke="currentColor" stroke-width="5"/>
              <line x1="20" y1="20" x2="80" y2="80" stroke="currentColor" stroke-width="3"/>
              <line x1="80" y1="20" x2="20" y2="80" stroke="currentColor" stroke-width="3"/>`,
  },
  STAIR_UP: {
    id: "STAIR_UP", label: "Staircase (UP)", layer: "structural", category: "Structural",
    size: 2500, snapToWall: false,
    svgPath: `<rect x="5" y="5" width="90" height="90" rx="2" fill="none" stroke="currentColor" stroke-width="4"/>
              <line x1="5"  y1="21" x2="95" y2="21" stroke="currentColor" stroke-width="2.5"/>
              <line x1="5"  y1="37" x2="95" y2="37" stroke="currentColor" stroke-width="2.5"/>
              <line x1="5"  y1="53" x2="95" y2="53" stroke="currentColor" stroke-width="2.5"/>
              <line x1="5"  y1="69" x2="95" y2="69" stroke="currentColor" stroke-width="2.5"/>
              <line x1="5"  y1="85" x2="95" y2="85" stroke="currentColor" stroke-width="2.5"/>
              <path d="M38,95 L50,78 L62,95" fill="currentColor"/>
              <text x="50" y="12" text-anchor="middle" font-size="10" font-family="monospace" fill="currentColor" font-weight="700">UP</text>`,
  },
};

// Grouped for picker UI
export const SYMBOL_CATEGORIES = [
  {
    id: "Electrical",
    color: "#facc15",
    symbols: Object.values(SYMBOL_LIBRARY).filter(s => s.category === "Electrical"),
  },
  {
    id: "Plumbing",
    color: "#22d3ee",
    symbols: Object.values(SYMBOL_LIBRARY).filter(s => s.category === "Plumbing"),
  },
  {
    id: "Furniture",
    color: "#a78bfa",
    symbols: Object.values(SYMBOL_LIBRARY).filter(s => s.category === "Furniture"),
  },
  {
    id: "Structural",
    color: "#f97316",
    symbols: Object.values(SYMBOL_LIBRARY).filter(s => s.category === "Structural"),
  },
];
